import { z } from 'zod';
import { paginationQuery } from '../api.js';
import { nullableText, optionalText } from '../common.js';
/**
 * Finanzierungen und Leasings (Modul `financing`, KON-253): Der Mandant verwaltet seine laufenden Darlehen, Ratenkäufe und Leasingverträge. Aus den Vertragsdaten entsteht ein Zahlungsplan
 * (`buildSchedule`), die Raten lassen sich als bezahlt vermerken, und die Zahlen fließen in den Finanzreport, die Auswertungen und die Vorschau der kommenden Zahlungen ein.
 * Alle Beträge in Cent. `paymentCents` ist immer der Betrag, der vom Konto geht (brutto).
 */
export const FINANCING_KINDS = ['loan', 'hire_purchase', 'lease'];
export const FINANCING_KIND_LABELS = { loan: 'Darlehen', hire_purchase: 'Ratenkauf', lease: 'Leasing' };
export const FINANCING_STATUSES = ['active', 'ended', 'terminated'];
export const FINANCING_STATUS_LABELS = { active: 'Laufend', ended: 'Beendet', terminated: 'Vorzeitig beendet' };
export const FINANCING_OBJECT_KINDS = ['vehicle', 'equipment', 'other'];
export const FINANCING_OBJECT_LABELS = { vehicle: 'Fahrzeug', equipment: 'Betriebsmittel', other: 'Sonstiges' };
export const INSTALLMENT_KINDS = ['regular', 'down_payment', 'balloon', 'fee'];
export const INSTALLMENT_KIND_LABELS = { regular: 'Rate', down_payment: 'Sonderzahlung', balloon: 'Schlussrate', fee: 'Gebühr' };
/** Bei Leasing sind die Raten Betriebsausgaben (Leasingaufwand), bei Darlehen und Ratenkauf nur der Zinsanteil. Die Tilgung ist keine Ausgabe, das Gut steht beim Mandanten. */
export const FINANCING_EXPENSE_NOTES = {
    loan: 'Als Aufwand zählen nur die Zinsen und Gebühren, die Tilgung nicht.',
    hire_purchase: 'Als Aufwand zählen nur die Zinsen und Gebühren, die Tilgung nicht. Das gekaufte Gut schreibt der Mandant über die Anschaffung ab.',
    lease: 'Als Aufwand zählen die Leasingraten (netto). Eine Sonderzahlung verteilt Wirkia gleichmäßig auf die Laufzeit.',
};
const KIND_ORDER = { fee: 0, down_payment: 1, regular: 2, balloon: 3 };
/** Tag der n-ten Rate: gleicher Tag im Monat wie die erste, in kürzeren Monaten der letzte Tag */
export function installmentDate(startDate, index) {
    const [y, m, d] = startDate.split('-').map(Number);
    const total = (m - 1) + index;
    const year = y + Math.floor(total / 12);
    const month = (total % 12) + 1;
    const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
    return `${year}-${String(month).padStart(2, '0')}-${String(Math.min(d, last)).padStart(2, '0')}`;
}
const netOf = (grossCents, taxRate) => (taxRate === 0 ? grossCents : Math.round(grossCents / (1 + taxRate / 100)));
/** Zahlungsplan aus den Vertragsdaten. Rein und ohne Gleitkomma-Fehler im Ergebnis: Jede Zahl ist auf Cent gerundet, die letzte Rate gleicht Rundungsreste aus. */
export function buildSchedule(input) {
    const n = input.termMonths;
    const rows = [];
    const fail = (error) => ({ rows: [], installmentCents: input.installmentCents ?? 0, error });
    if (!Number.isInteger(n) || n < 1)
        return fail('Die Laufzeit muss mindestens einen Monat betragen.');
    if (input.kind === 'lease') {
        const gross = input.installmentCents;
        if (!gross || gross <= 0)
            return fail('Bei Leasing gehört die monatliche Rate zum Vertrag.');
        const net = netOf(gross, input.taxRate);
        const tax = gross - net;
        const downNet = netOf(input.downPaymentCents, input.taxRate);
        const downTax = input.downPaymentCents - downNet;
        const spread = Math.floor(downNet / n);
        let remaining = gross * n;
        if (input.feesCents > 0)
            rows.push({ seq: 0, kind: 'fee', dueDate: input.startDate, paymentCents: input.feesCents, interestCents: 0, principalCents: 0, netCents: input.feesCents, taxCents: 0, balanceCents: remaining });
        if (input.downPaymentCents > 0)
            rows.push({ seq: 0, kind: 'down_payment', dueDate: input.startDate, paymentCents: input.downPaymentCents, interestCents: 0, principalCents: 0, netCents: 0, taxCents: downTax, balanceCents: remaining });
        for (let k = 0; k < n; k++) {
            remaining -= gross;
            const share = k === n - 1 ? downNet - spread * (n - 1) : spread;
            rows.push({ seq: 0, kind: 'regular', dueDate: installmentDate(input.startDate, k), paymentCents: gross, interestCents: 0, principalCents: 0, netCents: net + share, taxCents: tax, balanceCents: remaining });
        }
    }
    else {
        const principal = input.principalCents;
        if (!principal || principal <= 0)
            return fail('Der Darlehensbetrag oder Kaufpreis fehlt.');
        const balloon = input.balloonCents;
        if (balloon < 0 || balloon > principal)
            return fail('Die Schlussrate darf nicht höher sein als der Darlehensbetrag.');
        const monthly = (input.annualRateBp ?? 0) / 10_000 / 12;
        let pmt = input.installmentCents;
        if (!pmt) {
            if (input.annualRateBp === null)
                return fail('Bitte geben Sie den Zinssatz oder die monatliche Rate an.');
            pmt = monthly === 0 ? Math.round((principal - balloon) / n) : Math.round(((principal - balloon / (1 + monthly) ** n) * monthly) / (1 - (1 + monthly) ** -n));
        }
        if (pmt <= 0)
            return fail('Die Rate muss größer als 0 sein.');
        if (input.feesCents > 0)
            rows.push({ seq: 0, kind: 'fee', dueDate: input.startDate, paymentCents: input.feesCents, interestCents: 0, principalCents: 0, netCents: input.feesCents, taxCents: 0, balanceCents: principal });
        let balance = principal;
        let lastDate = input.startDate;
        for (let k = 0; k < n && balance > balloon; k++) {
            const interest = Math.round(balance * monthly);
            let repay = pmt - interest;
            if (repay <= 0 && k === 0)
                return fail('Die Rate deckt die Zinsen nicht. Bitte prüfen Sie Rate, Zinssatz und Darlehensbetrag.');
            // Die letzte Rate gleicht Rundungsreste aus, eine zu hohe Rate beendet das Darlehen früher
            if (k === n - 1 || repay > balance - balloon)
                repay = balance - balloon;
            balance -= repay;
            lastDate = installmentDate(input.startDate, k);
            rows.push({ seq: 0, kind: 'regular', dueDate: lastDate, paymentCents: interest + repay, interestCents: interest, principalCents: repay, netCents: interest, taxCents: 0, balanceCents: balance });
        }
        if (balloon > 0)
            rows.push({ seq: 0, kind: 'balloon', dueDate: lastDate, paymentCents: balloon, interestCents: 0, principalCents: balloon, netCents: 0, taxCents: 0, balanceCents: 0 });
    }
    rows.sort((a, b) => a.dueDate.localeCompare(b.dueDate) || KIND_ORDER[a.kind] - KIND_ORDER[b.kind]);
    rows.forEach((r, i) => (r.seq = i + 1));
    return { rows, installmentCents: input.kind === 'lease' ? input.installmentCents : (rows.find((r) => r.kind === 'regular')?.paymentCents ?? 0), error: null };
}
// ---------------------------------------------------------------------------
// Verträge
// ---------------------------------------------------------------------------
const cents = (label) => z.number(`Bitte ${label} als Zahl angeben`).int(`Bitte ${label} in ganzen Cent angeben`).min(0, `${label} darf nicht negativ sein`).max(100_000_000_000, `${label} ist zu hoch`);
const isoDate = z.iso.date('Bitte ein Datum angeben');
/** Felder eines Vertrags. Beim Anlegen gelten Vorgaben, beim Ändern bleibt ein fehlendes Feld, wie es ist (deshalb dort ohne Vorgaben). */
function financingFields(forCreate) {
    const d = (schema, value) => (forCreate ? schema.default(value) : schema.optional());
    return {
        kind: z.enum(FINANCING_KINDS, 'Bitte die Art wählen'),
        title: z.string('Bitte eine Bezeichnung angeben').trim().min(2, 'Bitte eine Bezeichnung angeben').max(150),
        lenderName: z.string('Bitte den Geldgeber oder Leasinggeber angeben').trim().min(1, 'Bitte den Geldgeber oder Leasinggeber angeben').max(150),
        lenderContactId: z.uuid('Ungültiger Kontakt').nullable().optional(),
        contractNumber: nullableText(100),
        objectKind: d(z.enum(FINANCING_OBJECT_KINDS, 'Bitte den Gegenstand wählen'), 'vehicle'),
        vehicleId: z.uuid('Ungültiges Fahrzeug').nullable().optional(),
        objectDescription: nullableText(200),
        startDate: isoDate,
        termMonths: z.number('Bitte die Laufzeit in Monaten angeben').int('Bitte ganze Monate angeben').min(1, 'Mindestens 1 Monat').max(360, 'Höchstens 360 Monate'),
        principalCents: cents('den Darlehensbetrag').nullable().optional(),
        downPaymentCents: d(cents('die Sonderzahlung'), 0),
        annualRateBp: z.number('Bitte den Zinssatz als Zahl angeben').int().min(0, 'Der Zinssatz darf nicht negativ sein').max(3000, 'Höchstens 30 % Zinsen').nullable().optional(),
        installmentCents: cents('die Rate').nullable().optional(),
        balloonCents: d(cents('die Schlussrate'), 0),
        feesCents: d(cents('die Gebühren'), 0),
        taxRate: d(z.union([z.literal(0), z.literal(7), z.literal(19)], 'Bitte 0, 7 oder 19 % wählen'), 19),
        inputTaxDeductible: d(z.boolean(), true),
        directDebit: d(z.boolean(), true),
        mileagePerYearKm: z.number().int().min(0).max(1_000_000).nullable().optional(),
        excessKmCents: z.number().int().min(0).max(10_000).nullable().optional(),
        ledgerAccount: nullableText(20),
        note: nullableText(2000),
    };
}
export const createFinancingInput = z
    .object({
    ...financingFields(true),
    /** Bestehender Vertrag ohne Lastschrift: Alle Raten, die bis heute fällig waren, gelten als bezahlt (mit dem Fälligkeitstag als Zahlungsdatum) */
    markPastPaid: z.boolean().optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.kind === 'lease' && !v.installmentCents)
        ctx.addIssue({ code: 'custom', path: ['installmentCents'], message: 'Bei Leasing gehört die monatliche Rate zum Vertrag' });
    if (v.kind !== 'lease' && !v.principalCents)
        ctx.addIssue({ code: 'custom', path: ['principalCents'], message: 'Bitte den Darlehensbetrag oder Kaufpreis angeben' });
    if (v.kind !== 'lease' && v.annualRateBp == null && !v.installmentCents)
        ctx.addIssue({ code: 'custom', path: ['annualRateBp'], message: 'Bitte den Zinssatz oder die monatliche Rate angeben' });
});
/** Ändern: alle Felder wie beim Anlegen, jedes optional. Ändern sich Beträge, Zinssatz, Laufzeit oder Beginn, rechnet Wirkia die noch nicht bezahlten Raten neu. */
export const updateFinancingInput = z
    .object({ ...Object.fromEntries(Object.entries(financingFields(false)).map(([k, v]) => [k, v.optional()])), status: z.enum(FINANCING_STATUSES).optional() })
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
export const listFinancingQuery = paginationQuery.extend({
    q: optionalText(100),
    kind: z.enum(FINANCING_KINDS).optional(),
    status: z.enum(FINANCING_STATUSES).optional(),
    vehicleId: z.uuid().optional(),
    sort: z.string().max(40).optional(),
});
/** Rate als bezahlt vermerken (Datum und Betrag, falls abweichend) oder mit `paid: false` zurücknehmen */
export const markInstallmentInput = z
    .object({ paid: z.boolean().default(true), paidOn: isoDate.optional(), paidCents: cents('den Betrag').optional() })
    .strict();
/** Alle Raten bis zu einem Tag als bezahlt vermerken (für Verträge ohne Lastschrift) */
export const markPaidUntilInput = z.object({ until: isoDate }).strict();
//# sourceMappingURL=financing.js.map